export interface CustomerDisplaySettings {
  enabled: boolean;
}

export interface CustomerDisplayLine {
  product_id: number | string | null;
  name: string;
  photo: string | null;
  quantity: number;
  unit_price: number;
  total_line: number;
  has_promotion: boolean;
}

export interface CustomerDisplaySale {
  id: number | string | null;
  reference: string;
  status: string;
  customer_name: string;
  subtotal_amount: number;
  discount_amount: number;
  total_amount: number;
  amount_received: number | null;
  amount_returned: number | null;
  payment_method: string | null;
  items: CustomerDisplayLine[];
}

export interface CustomerDisplayState {
  version: 1;
  enabled: boolean;
  shop_id: number | null;
  shop_name: string;
  cashier_name: string;
  updated_at: string;
  sale: CustomerDisplaySale | null;
}

interface BuildCustomerDisplayStatePayload {
  enabled: boolean;
  sale?: Record<string, unknown> | null;
  shopId?: number | string | null;
  shopName?: string | null;
  cashierName?: string | null;
}

export const CUSTOMER_DISPLAY_CHANNEL = 'suite241_gestion_commerciale_customer_display';
export const CUSTOMER_DISPLAY_STATE_KEY = 'suite241_gestion_commerciale_customer_display_state';
export const CUSTOMER_DISPLAY_SETTINGS_KEY = 'suite241_gestion_commerciale_customer_display_settings';
export const CUSTOMER_DISPLAY_SETTINGS_UPDATED_EVENT = 'suite241:customer-display-settings-updated';

export const DEFAULT_CUSTOMER_DISPLAY_SETTINGS: CustomerDisplaySettings = {
  enabled: false,
};

const isObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const asString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (value === null || typeof value === 'undefined') return fallback;

  return String(value);
};

const numberOrZero = (value: unknown): number => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const nullableNumber = (value: unknown): number | null => {
  if (value === null || typeof value === 'undefined' || value === '') return null;

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const nullableId = (value: unknown): number | string | null => {
  if (value === null || typeof value === 'undefined' || value === '') return null;

  return typeof value === 'number' || typeof value === 'string' ? value : String(value);
};

const normalizeLine = (item: Record<string, unknown>): CustomerDisplayLine => {
  const product = isObject(item.product) ? item.product : {};
  const unitPrice = numberOrZero(item.unit_price);
  const quantity = numberOrZero(item.quantity);

  return {
    product_id: nullableId(item.product_id || item.id),
    name: asString(item.product_name || item.name || product.name, 'Produit'),
    photo: asString(item.product_photo || item.photo || product.photo || product.image || '', '') || null,
    quantity,
    unit_price: unitPrice,
    total_line: numberOrZero(item.total_line || quantity * unitPrice),
    has_promotion: numberOrZero(item.base_unit_price) > unitPrice
      || ['planned_promotion', 'cashier_promotion'].includes(asString(item.price_source)),
  };
};

const normalizeSale = (sale: unknown): CustomerDisplaySale | null => {
  if (!isObject(sale)) return null;

  const client = isObject(sale.client) ? sale.client : {};
  const customer = isObject(sale.customer) ? sale.customer : {};

  return {
    id: nullableId(sale.id),
    reference: asString(sale.reference, 'Nouvelle vente'),
    status: asString(sale.status, 'draft'),
    customer_name: asString(sale.customer_name || client.name || customer.name, 'comptant'),
    subtotal_amount: numberOrZero(sale.subtotal_amount || sale.total_amount),
    discount_amount: numberOrZero(sale.discount_amount),
    total_amount: numberOrZero(sale.total_amount),
    amount_received: nullableNumber(sale.amount_received),
    amount_returned: nullableNumber(sale.amount_returned ?? sale.change_due),
    payment_method: asString(sale.payment_method, '') || null,
    items: Array.isArray(sale.items) ? sale.items.filter(isObject).map((item) => normalizeLine(item)) : [],
  };
};

const normalizeState = (value: unknown): CustomerDisplayState | null => {
  if (!isObject(value)) return null;

  const shopId = nullableNumber(value.shop_id);

  return {
    version: 1,
    enabled: value.enabled === true,
    shop_id: shopId,
    shop_name: asString(value.shop_name, ''),
    cashier_name: asString(value.cashier_name, ''),
    updated_at: asString(value.updated_at, new Date().toISOString()),
    sale: normalizeSale(value.sale),
  };
};

const createBroadcastChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
    return null;
  }

  try {
    return new window.BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);
  } catch {
    return null;
  }
};

let publishChannel: BroadcastChannel | null | undefined;

const getPublishChannel = (): BroadcastChannel | null => {
  if (typeof publishChannel !== 'undefined') return publishChannel;

  publishChannel = createBroadcastChannel();

  return publishChannel;
};

export const loadCustomerDisplaySettings = (): CustomerDisplaySettings => {
  if (typeof window === 'undefined') return { ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS };

  try {
    const raw = window.localStorage.getItem(CUSTOMER_DISPLAY_SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    return {
      ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS,
      enabled: parsed.enabled === true,
    };
  } catch {
    return { ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS };
  }
};

export const saveCustomerDisplaySettings = (
  settings: Partial<CustomerDisplaySettings>
): CustomerDisplaySettings => {
  const normalized = {
    ...DEFAULT_CUSTOMER_DISPLAY_SETTINGS,
    ...settings,
    enabled: settings.enabled === true,
  };

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CUSTOMER_DISPLAY_SETTINGS_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent(CUSTOMER_DISPLAY_SETTINGS_UPDATED_EVENT, {
      detail: normalized,
    }));
  }

  return normalized;
};

export const buildCustomerDisplayState = (
  payload: BuildCustomerDisplayStatePayload
): CustomerDisplayState => ({
  version: 1,
  enabled: payload.enabled === true,
  shop_id: nullableNumber(payload.shopId),
  shop_name: asString(payload.shopName, ''),
  cashier_name: asString(payload.cashierName, ''),
  updated_at: new Date().toISOString(),
  sale: payload.enabled === true ? normalizeSale(payload.sale) : null,
});

export const readCustomerDisplayState = (): CustomerDisplayState | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(CUSTOMER_DISPLAY_STATE_KEY);

    return raw ? normalizeState(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

export const publishCustomerDisplayState = (state: CustomerDisplayState): void => {
  if (typeof window === 'undefined') return;

  const normalized = normalizeState(state);
  if (!normalized) return;

  window.localStorage.setItem(CUSTOMER_DISPLAY_STATE_KEY, JSON.stringify(normalized));
  getPublishChannel()?.postMessage(normalized);
};

export const subscribeCustomerDisplay = (
  listener: (state: CustomerDisplayState) => void
): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  let closed = false;
  const channel = createBroadcastChannel();
  const emit = (state: CustomerDisplayState | null) => {
    if (!closed && state) {
      listener(state);
    }
  };

  if (channel) {
    channel.onmessage = (event) => emit(normalizeState(event.data));
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== CUSTOMER_DISPLAY_STATE_KEY || !event.newValue) return;

    try {
      emit(normalizeState(JSON.parse(event.newValue)));
    } catch {
      emit(null);
    }
  };

  window.addEventListener('storage', onStorage);

  return () => {
    closed = true;
    window.removeEventListener('storage', onStorage);
    channel?.close();
  };
};
